import { type ChangeEvent, useEffect, useMemo, useState } from 'react';
import Button from '@components/Button';
import Icon from '@components/Icon';
import InputText from '@components/InputText';
import Modal from '@components/Modal';
import type { RadioCardOption } from '@components/RadioCardGroup';
import { RadioCardGroup } from '@components/RadioCardGroup';
import { Show } from '@components/Show';
import { Stepper } from '@components/Stepper';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { useBlockLibrary } from '@queries/fetchBlocks';
import { capitalize } from 'lodash-es';

interface SaveAsNewChildBlockModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: (name: string, isChildBlockType: boolean) => void;
}

type BlockType = 'child' | 'parent';

enum SaveAsNewSteps {
  SelectBlockType = 1,
  NameBlock = 2,
}

export const SaveAsNewChildBlockModal = ({ isOpen, isLoading, onClose, onConfirm }: SaveAsNewChildBlockModalProps) => {
  const { formValues, currentBlock } = useBlockEditFormStore(({ formValues, currentBlock }) => ({
    formValues,
    currentBlock,
  }));

  const [currentStep, setCurrentStep] = useState<SaveAsNewSteps>(SaveAsNewSteps.SelectBlockType);
  const [name, setName] = useState(formValues?.name ?? '');
  const [selectedBlockType, setSelectedBlockType] = useState<BlockType>('child');

  const isFirstStep = currentStep === SaveAsNewSteps.SelectBlockType;
  const isLastStep = currentStep === SaveAsNewSteps.NameBlock;

  const getStepperItemState = (stepIndex: number) => {
    if (stepIndex === currentStep) return { isActive: true };
    if (stepIndex < currentStep) return { isCompleted: true };
    return {};
  };

  const onNext = () => setCurrentStep(SaveAsNewSteps.NameBlock);
  const onPrevious = () => {
    if (isFirstStep) {
      onClose();
    } else {
      setCurrentStep(SaveAsNewSteps.SelectBlockType);
    }
  };

  const { data: parentBlock, isFetching } = useBlockLibrary(
    currentBlock?.parentBlockId ?? '',
    currentBlock?.parentBlockId !== undefined && isOpen,
  );

  useEffect(() => {
    setName(formValues?.name ?? '');
    setCurrentStep(SaveAsNewSteps.SelectBlockType);
    setSelectedBlockType('child');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formValues is intentionally excluded — we only want to snapshot the name at open time
  }, [isOpen]);

  const parentBlockName = parentBlock?.name ?? currentBlock?.name;
  const blockTypeOptions: RadioCardOption<BlockType>[] = useMemo(
    () => [
      {
        value: 'child',
        title: 'New Child Version',
        description: `Create a child of ${parentBlockName}`,
        dataTestId: 'block-type-child',
      },
      {
        value: 'parent',
        title: 'New Parent Block',
        description: 'Create a new separate parent block',
        dataTestId: 'block-type-parent',
      },
    ],
    [parentBlockName],
  );

  const isChildBlockType = selectedBlockType === 'child';

  if (
    currentBlock === undefined ||
    (currentBlock?.parentBlockId !== undefined && parentBlock === undefined) ||
    isFetching
  ) {
    return null;
  }

  const handleChangeBlockName = (event: ChangeEvent<HTMLInputElement>) => setName(event.target.value);

  return (
    <Modal
      title="Save as new block"
      onClose={onClose}
      open={isOpen}
      className="w-[572px] gap-0 sm:max-w-[572px]"
      titleClassName="pt-4 text-lg leading-6 font-medium justify-center!"
      data-testid="save-as-new-block-modal"
      shouldShowCloseIcon={false}
    >
      <div className="flex min-w-0 flex-col items-center justify-center gap-6 px-8 pt-0 pb-6">
        <p className="text-sm leading-5 font-normal text-gray-500">This will create a new version of this block.</p>
        <Stepper getStepperItemState={getStepperItemState} size={SaveAsNewSteps.NameBlock} />
        <Show when={currentStep === SaveAsNewSteps.SelectBlockType}>
          <RadioCardGroup
            labelText="Select block type"
            options={blockTypeOptions}
            value={selectedBlockType}
            onChange={setSelectedBlockType}
          />
        </Show>
        <Show when={currentStep === SaveAsNewSteps.NameBlock}>
          <div className="flex w-full flex-col gap-5">
            <Show when={isChildBlockType}>
              <div className="flex items-center gap-1">
                <Icon name="block-parent" aria-hidden="true" className="h-5 shrink-0 text-gray-400" />
                <span className="text-sm font-medium text-gray-500 hover:text-gray-700">{parentBlockName}</span>
              </div>
            </Show>
            <InputText
              labelText={`Name ${selectedBlockType} block`}
              placeholder={`${capitalize(selectedBlockType)} block name`}
              name={`${selectedBlockType}-block-name`}
              onChange={handleChangeBlockName}
              value={name}
            />
          </div>
        </Show>
        <div className="mt-4 flex w-full justify-between gap-4">
          <Button className="h-9.5 w-full" isDisabled={isLoading} onClick={onPrevious}>
            {isFirstStep ? 'Cancel' : 'Back'}
          </Button>
          <Button
            kind="primary"
            loading={isLoading}
            className="h-9.5 w-full"
            onClick={isLastStep ? () => onConfirm(name, isChildBlockType) : onNext}
          >
            {isLastStep ? 'Create' : 'Next'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
